/**
 * Customer Card Settings Tab Component
 * Controls which elements appear on each card in the approved customer directory (/customers).
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import {
  Image, ShieldAlert, TrendingUp, Star, ClipboardList, Users,
  type LucideIcon,
} from 'lucide-react';
import type { CustomerCardFieldsVisibility } from './types';

interface FieldDefinition {
  id: keyof CustomerCardFieldsVisibility;
  label: string;
  description: string;
  icon: LucideIcon;
}

const CUSTOMER_CARD_FIELDS: FieldDefinition[] = [
  {
    id: 'logo',
    label: 'Company Logo',
    description: 'Show the supplier company logo at the top of each card.',
    icon: Image,
  },
  {
    id: 'risk_badge',
    label: 'Risk Badge',
    description: 'Show the risk-level indicator (Critical / High / Medium / Low).',
    icon: ShieldAlert,
  },
  {
    id: 'otd',
    label: 'On-Time Delivery (OTD) %',
    description: 'Show the on-time delivery percentage metric.',
    icon: TrendingUp,
  },
  {
    id: 'rating',
    label: 'Overall Rating',
    description: 'Show the aggregated supplier performance rating.',
    icon: Star,
  },
  {
    id: 'questionnaire_status',
    label: 'Questionnaire Status',
    description: 'Show the compliance badge summarising questionnaire freshness (up to date / expiring / overdue / pending).',
    icon: ClipboardList,
  },
];

interface CustomerCardTabProps {
  customerCardFields: CustomerCardFieldsVisibility;
  saving: boolean;
  onCustomerCardFieldsChange: (fields: CustomerCardFieldsVisibility) => void;
  onSave: () => void;
}

export function CustomerCardTab({
  customerCardFields,
  saving,
  onCustomerCardFieldsChange,
  onSave,
}: CustomerCardTabProps) {
  const enabledCount = CUSTOMER_CARD_FIELDS.filter(f => customerCardFields[f.id] ?? true).length;
  const totalCount = CUSTOMER_CARD_FIELDS.length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Supplier Card Elements
          </CardTitle>
          <CardDescription>
            Control which elements appear on each supplier card in the approved supplier directory (/suppliers).
          </CardDescription>
          <div className="flex items-center gap-4 pt-2 text-sm text-muted-foreground">
            <span>{enabledCount}/{totalCount} elements enabled</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {CUSTOMER_CARD_FIELDS.map((field) => {
            const Icon = field.icon;
            return (
              <div
                key={field.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <Icon className="h-5 w-5 text-primary" />
                  <div>
                    <Label className="text-base font-medium">{field.label}</Label>
                    <p className="text-sm text-muted-foreground">{field.description}</p>
                  </div>
                </div>
                <Switch
                  checked={customerCardFields[field.id] ?? true}
                  onCheckedChange={(checked) =>
                    onCustomerCardFieldsChange({ ...customerCardFields, [field.id]: checked })
                  }
                />
              </div>
            );
          })}

          <Button onClick={onSave} disabled={saving} className="w-full mt-4">
            {saving ? 'Saving...' : 'Save Supplier Card Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
